<script setup>
import {computed, onMounted, ref} from "vue";
import {usePiniaStore} from "../store/pinia_store";
import _, {isEmpty} from "lodash";

const props = defineProps({
    crmId: {
        type: String,
        required: true,
    },
    dentaidApiInstance: {
        type: Function,
        required: true,
    },
    eventBus: {
        type: Object,
        required: true,
    },
    orderUnit: {
        type: Number,
        required: true,
    },
    isTeaser: {
        type: Boolean,
        default: false,
    },
    priceList: {
        type: Object,
        required: false,
    },
    detailPage: {
        type: Boolean,
        default: false,
    }
});

const piniaStore = usePiniaStore();
const loaded = ref(false);
const price = ref(props.priceList?.net_price / 100);
const priceOnePiece = ref(props.priceList?.unit_price / 100);
const quantityPrices = ref(props.priceList?.available_quantity_prices);
const minOrderQuantity = ref(props.priceList?.min_order_quantity);
const quantity = ref(props.orderUnit);
const language = ref(window.drupalSettings.ef2_custom?.language ?? 'nl');
const labelText = ref(window.Drupal.t('Price per item'));

const discountText = window.Drupal.t('korting');
const combinerenBulkVerpakkingenText = window.Drupal.t('Combining packaging is possible');
const staffelKortingText = window.Drupal.t('Staffelkorting');
const piecesText = window.Drupal.t('pieces');
const priceAgreementLabel = window.Drupal.t('Your price agreement');
const fromText = window.Drupal.t('from');

const isFrench = computed(() => {
    return language.value === 'fr';
});

const hasPrice = computed(() => {
    return !lodash.isNil(price.value);
})

const teaserPrice = computed(() => {
    if (lodash.isNil(priceOnePiece.value)) {
        return '';
    }

    price.value = parseFloat(priceOnePiece.value) * props.orderUnit;

    return price.value;
});

const computedOrderText = computed(() => {
    if (props.orderUnit > 1) {
        return window.Drupal.t('per @amount pieces', {
            '@amount': props.orderUnit,
        });
    }

    return window.Drupal.t('per @amount piece', {
        '@amount': props.orderUnit,
    });
})

const totalPrice = computed(() => {
    return priceOnePiece.value * quantity.value;
})

const hasQuantityDiscount = computed(() => {
    return !isEmpty(quantityPrices.value) && piniaStore.customer.has_quantity_discount;
})

const showPriceAgreement = computed(() => {
    return !isEmpty(quantityPrices.value) && !piniaStore.customer.has_quantity_discount;
})

const magnumDiscountInfo = computed(() => {
    return piniaStore.magnumDiscountInfo(
        props.crmId,
        quantity.value,
        quantityPrices.value,
        priceOnePiece.value
    );
});

const magnumDiscountText = computed(() => {
    return piniaStore.magnumDiscountText(magnumDiscountInfo.value);
});

const pricePrefix = (value) => {
    return ("" + value).split('.')[0];
}

const priceSuffix = (value) => {
    return ',' + ("" + value.toFixed(2)).split('.')[1];
}

const getPrice = () => {
    if (isEmpty(drupalSettings.ef2_custom.customer_number)) {
        loaded.value = true;

        return;
    }

    props.dentaidApiInstance.post(`/prices?token=${piniaStore.parsedToken}`, {
        items: {
            "0": {
                sku: props.crmId
            }
        }
    }).then(response => {
        price.value = response.data[props.crmId].net_price / 100;
        priceOnePiece.value = response.data[props.crmId].unit_price / 100;
        quantityPrices.value = response.data[props.crmId].available_quantity_prices;
        minOrderQuantity.value = response.data[props.crmId].min_order_quantity;

        props.eventBus.$emit('load-price', response.data[props.crmId]);

        setTimeout(() => {
            loaded.value = true;
        }, 250)
    })
}

onMounted(() => {
    if (!props.detailPage) {
        props.eventBus.$on('quantity-changed', (data) => {
            quantity.value = data.quantity;

            getPrice();
        });
    }

    if(isEmpty(props.priceList)) {
        getPrice();
    }
})
</script>

<template>
    <div class="vue-product-price">
        <template v-if="hasPrice && !isTeaser && loaded">
            <div v-if="hasQuantityDiscount || showPriceAgreement" class="mt-3 price-one-piece">
                <template v-if="hasQuantityDiscount">
                    <strong>{{ staffelKortingText }}</strong><br>
                    <span class="d-block" v-for="quantityPrice of quantityPrices">
                        {{ quantityPrice.quantity }} {{ piecesText }} - {{ quantityPrice.percentage }}{{ isFrench ? ' ' : '' }}% {{ discountText }}
                    </span>
                </template>
                <template v-else-if="showPriceAgreement">
                    <strong>{{ priceAgreementLabel }}</strong><br>
                    <span v-if="magnumDiscountText" class="d-block">
                        {{ magnumDiscountText }}
                    </span>
                    <span v-else class="d-block" v-for="quantityPrice of quantityPrices">
                        {{ quantityPrice.percentage }}{{ isFrench ? ' ' : '' }}% {{ discountText }} ({{ fromText }} {{ quantityPrice.quantity }} {{ piecesText }})
                    </span>
                </template>
                <em class="d-block">{{ combinerenBulkVerpakkingenText }}.</em>
            </div>

            <p class="price-one-piece mt-3">{{ labelText }} {{
                    pricePrefix(priceOnePiece)
                }}{{ priceSuffix(priceOnePiece) }} </p>
            <span class="price">
                {{ pricePrefix(totalPrice) }}

                <sup>
                    {{ priceSuffix(totalPrice) }}
                </sup>
            </span>
        </template>

        <template v-if="hasPrice && isTeaser">
            <p>{{ pricePrefix(teaserPrice) }}{{ priceSuffix(teaserPrice) }} {{ computedOrderText }}</p>
        </template>
    </div>
</template>
